import { defineStore } from "pinia";
import { computed } from "vue";
import { t } from "@/i18n";
import {
    BookIcon,
    CalendarIcon,
    LayoutSquare2Icon,
    LayoutVertical4Icon,
    StoreIcon,
    BusinessHandshakeIcon,
    UserMultipleIcon,
    PlayerIcon,
    CoinsHandIcon,
    Building2Icon,
    ChevronRightIcon,
    LayoutHorizontal2Icon,
    UserCircleSingleIcon,
} from "@easy/ui/icons";
import { makeLogger } from "@/shared/consola";
import { WALL_LAYOUTS } from "@/apps/love/services/walls.service";
import SvgLoveLayoutBoard from "@/apps/love/project/onboarding/svg-previews/SvgLoveLayoutBoard.vue";
import SvgLoveLayoutBigSlider from "@/apps/love/project/onboarding/svg-previews/SvgLoveLayoutBigSlider.vue";
import SvgLoveLayoutSlider from "@/apps/love/project/onboarding/svg-previews/SvgLoveLayoutSlider.vue";
import SvgLoveLayoutMultislider from "@/apps/love/project/onboarding/svg-previews/SvgLoveLayoutMultislider.vue";
import SvgLoveLayoutAvatars from "@/apps/love/project/onboarding/svg-previews/SvgLoveLayoutAvatars.vue";
import { useOnboarding } from "@/shared/projects/onboarding/useOnboarding";
import { useActions, useMutations, useState } from "@/store/vuex-composition-mappers";
import { makeEmptyReviewForWall } from "@/apps/love/model/Review";
import { mapThemeToLoveColors } from "@/shared/projects/prepare-theme";

const STORE_NAME = "love-creator";
const logger = makeLogger("🍍" + STORE_NAME);

export const useLoveCreatorStore = defineStore(STORE_NAME, () => {
    logger.trace("👋hello");

    const { saveSnapshot } = useActions("love/projects", ["saveSnapshot"]);
    const { wall } = useState("love", ["wall"]);
    const { showAlert } = useMutations(["showAlert"]);
    const { addTestimonial } = useMutations("love", ["addTestimonial"]);

    const {
        isCreatorShown,
        onboardState,
        previewCssVars,
        autoOpenCreator,
        toggleCreatorShown,
        updateProductInfo,
        validate,
    } = useOnboarding({
        // autoOpenRules: (testimonials) => testimonials.filter((t) => !t._deleted).length === 0,
        initialValues: {
            layout: WALL_LAYOUTS.board,
            hidePrompt: true, // for now (EAS-2196)
            mode: "by-prompt", // for now (EAS-2196)
        },
    });

    onboardState.promptDescription = "use:(empty)"; // for now (EAS-2196)

    const templates = computed(() => [
        {
            value: t("love.templates.empty"),
            id: "empty",
        },
        {
            value: t("love.templates.product"),
            icon: StoreIcon,
            id: "product",
        },
        {
            value: "E-book",
            icon: BookIcon,
            id: "e-book",
        },
        {
            value: t("love.templates.training"),
            icon: CalendarIcon,
            id: "training",
        },
        {
            value: t("love.templates.consultations"),
            icon: BusinessHandshakeIcon,
            id: "consultations",
        },
        {
            value: t("love.templates.community"),
            icon: UserMultipleIcon,
            id: "community",
        },
        {
            value: "Webinar",
            icon: PlayerIcon,
            id: "webinar",
        },
        {
            value: t("love.templates.service"),
            icon: CoinsHandIcon,
            id: "service",
        },
        {
            value: t("love.templates.event"),
            icon: Building2Icon,
            id: "event",
        },
    ]);

    const layouts = computed(() => [
        {
            value: WALL_LAYOUTS.board,
            label: t("love.wall.board"),
            info: t("love.wall.board_info"),
            icon: LayoutSquare2Icon,
            preview: SvgLoveLayoutBoard,
        },
        {
            value: WALL_LAYOUTS.bigslider,
            label: t("love.wall.bigslider"),
            info: t("love.wall.bigslider_info"),
            icon: ChevronRightIcon,
            preview: SvgLoveLayoutBigSlider,
        },
        {
            value: WALL_LAYOUTS.slider,
            label: t("love.wall.slider"),
            info: t("love.wall.slider_info"),
            icon: LayoutHorizontal2Icon,
            preview: SvgLoveLayoutSlider,
        },
        {
            value: WALL_LAYOUTS.multislider,
            label: t("love.wall.multislider"),
            info: t("love.wall.multislider_info"),
            icon: LayoutVertical4Icon,
            preview: SvgLoveLayoutMultislider,
        },
        {
            value: WALL_LAYOUTS.avatars,
            label: t("love.wall.avatars"),
            info: t("love.wall.avatars_info"),
            icon: UserCircleSingleIcon,
            preview: SvgLoveLayoutAvatars,
        },
    ]);

    const previewComponent = computed(
        () => layouts.value.find((l) => l.value === onboardState.layout)?.preview
    );

    return {
        onboardState,
        isCreatorShown,
        templates,
        layouts,
        previewCssVars,
        previewComponent,
        links: computed(() => ({
            video: "",
            docs: "",
        })),
        autoOpenCreator,
        onboardingAction(resolveResponse) {
            return async () => {
                if (!validate()) {
                    return;
                }
                const { theme, layout, logo, template } = onboardState;
                if (template === "empty") {
                    addTestimonial({
                        ...makeEmptyReviewForWall(wall),
                        name: "Josh Sample",
                        content: "....",
                        isPublished: true,
                    });
                } else {
                    try {
                        onboardState.isLoading = true;
                        const { result = [] } = await resolveResponse();
                        logger.debug("about to fillWithExample", { result, theme, layout });
                        for (const { name, text, position, website, photo, rate } of result) {
                            addTestimonial({
                                ...makeEmptyReviewForWall(wall),
                                name,
                                content: text,
                                position,
                                www: website,
                                photo,
                                rate,
                                isPublished: true,
                            });
                        }
                    } catch (e) {
                        logger.error(e);
                    } finally {
                        onboardState.isLoading = false;
                    }
                }
                if (logo.squareLink || logo.link) {
                    wall.value.askImg = logo.squareLink || logo.link;
                }
                wall.value.layout = layout;
                for (const [key, value] of Object.entries(mapThemeToLoveColors(theme))) {
                    wall.value[key] = value;
                }
                showAlert({
                    type: "success",
                    title: t("shared.creator.generation_success"),
                });
                await saveSnapshot();
                toggleCreatorShown(false);
            };
        },
        toggleCreatorShown,
        updateProductInfo,
        validate,
    };
});
